import React from 'react';
import { Import } from 'lucide-react';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import Alert from 'src/shared/components/ui/alert';
import { ImportResultCallout } from '../../import-result-callout/ImportResultCallout';
import type { ImportResult } from '../../../hooks/useImportSites.types';

type ImportProjectsStepProps = {
    projectCount: number;
    onImport: () => Promise<void>;
    onContinue: () => void;
    isImporting: boolean;
    progress: { current: number; total: number } | null;
    /** Once an import has completed, the result replaces the Import button so
     *  the user sees what happened before advancing. Absent before the first
     *  import attempt and during in-flight imports. */
    result: ImportResult | null;
};

const ImportProjectsStep = ({ projectCount, onImport, onContinue, isImporting, progress, result }: ImportProjectsStepProps) => {
    const siteWord = projectCount === 1 ? 'site' : 'sites';
    const hasProjects = projectCount > 0;
    const showResult = !isImporting && result !== null;

    return (
        <div className='flex flex-col items-center gap-6'>
            <div className='flex items-center justify-center w-20 h-20 rounded-full bg-grey-20'>
                <Import className='w-10 h-10 text-primary' />
            </div>
            <div className='flex flex-col items-center gap-2 max-w-lg text-center'>
                <h2 className='text-2xl font-semibold text-primary'>Import Sites</h2>
                {hasProjects ? (
                    <p className='text-base text-grey-70'>
                        Import sites from existing Global Nature Performance projects? <br /> This organisation has {projectCount}{' '}
                        {siteWord} available to import as drafts.
                    </p>
                ) : (
                    <p className='text-base text-grey-70'>Sites can be imported from existing Global Nature Performance projects.</p>
                )}
            </div>
            {!hasProjects && (
                <Alert severity='info' className='max-w-md' data-testid='no-projects-callout'>
                    This organisation has no existing projects to import. You can add sites manually in the next step.
                </Alert>
            )}
            {isImporting && progress ? (
                <div className='flex flex-col items-center gap-2 w-64'>
                    <p className='text-sm text-grey-70'>
                        Importing... ({progress.current}/{progress.total})
                    </p>
                    <div className='w-full h-1.5 bg-grey-20 rounded-full overflow-hidden'>
                        <div
                            className='h-full bg-primary rounded-full transition-all duration-200'
                            style={{ width: `${(progress.current / progress.total) * 100}%` }}
                            data-testid='import-progress-bar'
                        />
                    </div>
                </div>
            ) : showResult ? (
                <div className='flex flex-col items-center gap-4 w-full max-w-md'>
                    <ImportResultCallout result={result!} />
                    <PrimaryButton onClick={onContinue} data-testid='wizard-continue-button'>
                        Continue
                    </PrimaryButton>
                </div>
            ) : hasProjects ? (
                <div className='flex items-center gap-3'>
                    <PrimaryButton onClick={onImport} data-testid='wizard-import-button'>
                        Import {projectCount} {siteWord}
                    </PrimaryButton>
                    <SecondaryButton onClick={onContinue} data-testid='wizard-skip-button'>
                        Skip
                    </SecondaryButton>
                </div>
            ) : (
                <SecondaryButton onClick={onContinue} data-testid='wizard-skip-button'>
                    Continue
                </SecondaryButton>
            )}
        </div>
    );
};

export default ImportProjectsStep;
